import React, { useState } from 'react';
import { 
  Book, FileText, MessageSquare, DollarSign, Bell, 
  Search, ChevronDown, ChevronRight, HelpCircle, Mail,
  Upload, Download, PenTool, User, Shield, CheckCircle,
  Clock, Eye, Home, Users, Sparkles
} from 'lucide-react';
import { Input } from '../ui/input';

const ClientDocumentation = () => {
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [searchQuery, setSearchQuery] = useState('');
  const [showWhatsNew, setShowWhatsNew] = useState(true);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // What's New items for clients
  const whatsNewItems = [
    {
      title: 'Guided Portal Tour',
      description: 'New interactive walkthrough helps you learn all the portal features.',
      icon: <Sparkles className="w-4 h-4" />,
      isNew: true
    },
    {
      title: 'Help Center',
      description: 'Comprehensive documentation to help you get the most out of your portal.',
      icon: <Book className="w-4 h-4" />,
      isNew: true
    },
    {
      title: 'Improved Document Signing',
      description: 'Use "Apply to All" to quickly fill multiple signature fields at once.',
      icon: <PenTool className="w-4 h-4" />,
      isNew: false
    }
  ];

  const documentationSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Book className="w-5 h-5" />,
      content: [
        {
          title: 'Welcome to Your Client Portal',
          description: 'Your secure portal to communicate with PPMT Strategic Group, access documents, view invoices, and sign documents electronically.'
        },
        {
          title: 'First Login',
          description: 'When you first receive your portal invitation:',
          steps: [
            'Check your email for the invitation from PPMT Strategic Group',
            'Click the "Access Portal" link in the email',
            'Login with your email and the temporary password provided',
            'You\'ll be prompted to change your password on first login',
            'Complete the guided tour to learn about portal features'
          ]
        },
        {
          title: 'Navigation Overview',
          description: 'Your portal dashboard provides access to:',
          steps: [
            'Dashboard - Overview of your account and notifications',
            'Documents - View and upload files',
            'Messages - Communicate with your accountant',
            'Invoices - View and pay invoices',
            'Team - Manage users on your account (if enabled)',
            'Profile - Update your information'
          ]
        }
      ]
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: <FileText className="w-5 h-5" />,
      content: [
        {
          title: 'Viewing Documents',
          description: 'Access files shared by your accountant:',
          steps: [
            'Go to Documents section from the sidebar',
            'Browse files organized by tags/categories',
            'Click on any file to preview it',
            'Download files using the download button',
            'Filter documents by date or type'
          ]
        },
        {
          title: 'Uploading Documents',
          description: 'Share files with your accountant:',
          steps: [
            'Click "Upload" in the Documents section',
            'Select files from your computer',
            'Add optional tags for organization',
            'Click Upload to send files securely',
            'Your accountant will be notified of new uploads'
          ]
        },
        {
          title: 'Supported File Types',
          description: 'You can upload various file formats:',
          steps: [
            'Documents: PDF, Word, Excel, PowerPoint',
            'Images: JPG, PNG, GIF',
            'Other: CSV, TXT files',
            'Maximum file size: 50MB per file'
          ]
        }
      ]
    },
    {
      id: 'document-signing',
      title: 'Document Signing',
      icon: <PenTool className="w-5 h-5" />,
      content: [
        {
          title: 'Signing Requests',
          description: 'When your accountant needs your signature:',
          steps: [
            'You\'ll receive an email notification',
            'A notification badge appears on your dashboard',
            'Click the notification to view the document',
            'Review the document carefully before signing'
          ]
        },
        {
          title: 'How to Sign',
          description: 'Complete your signature:',
          steps: [
            'Open the signature request from your dashboard',
            'Fields requiring your input are highlighted',
            'For signature fields: Draw or type your signature',
            'For initial fields: Add your initials',
            'Date fields are automatically filled',
            'Click "Submit Signature" when complete'
          ]
        },
        {
          title: 'Tips for Signing',
          description: 'Make signing easier:',
          steps: [
            'Use "Apply to All" to fill multiple signature/initial fields at once',
            'You can redo your signature if not satisfied',
            'Preview before submitting',
            'You\'ll receive a confirmation email after signing',
            'Download the signed document for your records'
          ]
        }
      ]
    },
    {
      id: 'messages',
      title: 'Messages',
      icon: <MessageSquare className="w-5 h-5" />,
      content: [
        {
          title: 'Sending Messages',
          description: 'Communicate securely with your accountant:',
          steps: [
            'Go to Messages section',
            'Type your message in the compose box',
            'Optionally attach files to your message',
            'Click Send to deliver your message',
            'Your accountant will receive an email notification'
          ]
        },
        {
          title: 'Receiving Messages',
          description: 'When your accountant contacts you:',
          steps: [
            'You\'ll receive an email notification',
            'New messages appear in your Messages section',
            'Unread messages are highlighted',
            'Click to read and respond',
            'All message history is saved'
          ]
        },
        {
          title: 'Best Practices',
          description: 'Tips for effective communication:',
          steps: [
            'Be specific about your questions or requests',
            'Attach relevant documents to provide context',
            'Check messages regularly for updates',
            'Use portal messages for sensitive financial matters'
          ]
        }
      ]
    },
    {
      id: 'invoices',
      title: 'Invoices & Payments',
      icon: <DollarSign className="w-5 h-5" />,
      content: [
        {
          title: 'Viewing Invoices',
          description: 'Access your invoices:',
          steps: [
            'Go to Invoices section from your dashboard',
            'View all invoices with their status',
            'Status types: Draft, Sent, Viewed, Paid, Overdue',
            'Click on an invoice to see details',
            'Download invoice PDF for your records'
          ]
        },
        {
          title: 'Making Payments',
          description: 'Pay invoices securely online:',
          steps: [
            'Open the invoice you want to pay',
            'Click "Pay Now" button',
            'You\'ll be redirected to secure payment page',
            'Enter your card details (processed via Stripe)',
            'Receive confirmation email upon payment',
            'Invoice status updates to "Paid"'
          ]
        },
        {
          title: 'Payment History',
          description: 'Track your payment history:',
          steps: [
            'View all past payments in Invoice section',
            'Filter by date range or status',
            'Download payment receipts',
            'Contact your accountant for billing questions'
          ]
        }
      ]
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <Bell className="w-5 h-5" />,
      content: [
        {
          title: 'Email Notifications',
          description: 'You\'ll receive emails for:',
          steps: [
            'New documents shared with you',
            'New messages from your accountant',
            'Signature requests',
            'Invoice reminders',
            'Tax deadline reminders',
            'Payment confirmations'
          ]
        },
        {
          title: 'Dashboard Notifications',
          description: 'Important alerts on your dashboard:',
          steps: [
            'Pending signature requests',
            'Unread messages count',
            'Overdue invoices',
            'Recent activity summary'
          ]
        }
      ]
    },
    {
      id: 'team',
      title: 'Team Management',
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          title: 'Adding Team Members',
          description: 'Invite others to access your portal (if enabled):',
          steps: [
            'Go to Team section',
            'Click "Add Team Member"',
            'Enter their name, email, and role',
            'Roles: Admin (full access) or Member (limited access)',
            'They\'ll receive an invitation email'
          ]
        },
        {
          title: 'Managing Team',
          description: 'Control team member access:',
          steps: [
            'View all team members and their status',
            'Edit member roles and permissions',
            'Deactivate members who no longer need access',
            'Remove members from your account'
          ]
        }
      ]
    },
    {
      id: 'profile',
      title: 'Your Profile',
      icon: <User className="w-5 h-5" />,
      content: [
        {
          title: 'Updating Information',
          description: 'Keep your profile current:',
          steps: [
            'Click on your name in the sidebar',
            'Update contact information (name, phone)',
            'Add or update your company details',
            'Save changes when done'
          ]
        },
        {
          title: 'Changing Password',
          description: 'Keep your account secure:',
          steps: [
            'Go to Profile settings',
            'Enter your current password',
            'Enter and confirm new password',
            'Password must be at least 6 characters',
            'Click Save to update'
          ]
        }
      ]
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          title: 'Your Data is Secure',
          description: 'How we protect your information:',
          steps: [
            'All data is encrypted in transit (HTTPS)',
            'Secure authentication with JWT tokens',
            'Session timeout for inactive users',
            'Only you and authorized staff can access your data'
          ]
        },
        {
          title: 'Login Security',
          description: 'Best practices for account security:',
          steps: [
            'Use a strong, unique password',
            'Don\'t share your login credentials',
            'Log out when using shared computers',
            'Contact support if you notice suspicious activity'
          ]
        },
        {
          title: 'Google Login',
          description: 'Sign in with your Google account:',
          steps: [
            'Click "Sign in with Google" on the login page',
            'Select your Google account',
            'Your accounts will be linked automatically',
            'Use either password or Google login going forward'
          ]
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: <HelpCircle className="w-5 h-5" />,
      content: [
        {
          title: 'Can\'t Login',
          description: 'If you\'re having trouble logging in:',
          steps: [
            'Verify you\'re using the correct email address',
            'Check caps lock is off',
            'Use "Forgot Password" to reset your password',
            'Check spam folder for password reset email',
            'Contact your accountant if still having issues'
          ]
        },
        {
          title: 'Can\'t Upload Files',
          description: 'File upload issues:',
          steps: [
            'Check file size is under 50MB',
            'Ensure file type is supported',
            'Try refreshing the page',
            'Try a different browser if issues persist'
          ]
        },
        {
          title: 'Not Receiving Emails',
          description: 'If you\'re missing portal emails:',
          steps: [
            'Check your spam/junk folder',
            'Add noreply@ppmtgroup.com to contacts',
            'Verify your email address is correct in profile',
            'Contact support to verify email configuration'
          ]
        },
        {
          title: 'Payment Issues',
          description: 'If payment doesn\'t go through:',
          steps: [
            'Verify card details are correct',
            'Ensure card hasn\'t expired',
            'Check with your bank for any blocks',
            'Try a different payment method',
            'Contact your accountant for alternative payment options'
          ]
        }
      ]
    }
  ];

  const filteredSections = documentationSections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (section.title.toLowerCase().includes(query)) return true;
    return section.content.some(item => 
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      (item.steps && item.steps.some(step => step.toLowerCase().includes(query)))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <Book className="w-6 h-6 mr-2 text-teal-600" />
            Help Center
          </h3>
          <p className="text-gray-600 mt-1">How to use your client portal</p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="client-docs-search-input"
          />
        </div>
      </div>

      {/* Quick Links */}
      <div className="glass rounded-xl p-4">
        <h4 className="font-semibold text-gray-800 mb-3">Quick Links</h4>
        <div className="flex flex-wrap gap-2">
          {documentationSections.slice(0, 6).map(section => (
            <button
              key={section.id}
              onClick={() => {
                setExpandedSections([section.id]);
                document.getElementById(`client-section-${section.id}`)?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-teal-50 hover:text-teal-700 rounded-lg text-sm transition-colors"
            >
              {section.icon}
              <span>{section.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* What's New Section */}
      {showWhatsNew && (
        <div className="glass rounded-xl overflow-hidden border-2 border-teal-200" data-testid="client-whats-new-section">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5" />
              <h4 className="font-semibold">What's New</h4>
            </div>
            <button 
              onClick={() => setShowWhatsNew(false)}
              className="text-white/80 hover:text-white text-sm"
            >
              Dismiss
            </button>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {whatsNewItems.map((item, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border transition-colors ${
                    item.isNew 
                      ? 'bg-teal-50 border-teal-200 hover:border-teal-300' 
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.isNew ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium text-gray-900 text-sm">{item.title}</h5>
                        {item.isNew && (
                          <span className="px-1.5 py-0.5 bg-teal-500 text-white text-xs rounded-full font-medium">
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Documentation Sections */}
      <div className="space-y-4">
        {filteredSections.map(section => (
          <div 
            key={section.id} 
            id={`client-section-${section.id}`}
            className="glass rounded-xl overflow-hidden"
            data-testid={`client-docs-section-${section.id}`}
          >
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center text-white">
                  {section.icon}
                </div>
                <span className="font-semibold text-gray-900">{section.title}</span>
              </div>
              {expandedSections.includes(section.id) ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Section Content */}
            {expandedSections.includes(section.id) && (
              <div className="border-t border-gray-100 p-4 space-y-6">
                {section.content.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500" />
                      {item.title}
                    </h4>
                    <p className="text-gray-600 text-sm ml-6">{item.description}</p>
                    {item.steps && (
                      <ul className="ml-6 space-y-1">
                        {item.steps.map((step, stepIndex) => (
                          <li 
                            key={stepIndex}
                            className="text-sm text-gray-600 flex items-start gap-2"
                          >
                            <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500 flex-shrink-0 mt-0.5">
                              {stepIndex + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="glass rounded-xl p-6 text-center">
        <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h4 className="font-semibold text-gray-900 mb-2">Need More Help?</h4>
        <p className="text-gray-600 text-sm mb-4">
          Can't find what you're looking for? Send a message to your accountant.
        </p>
        <a 
          href="/client/messages"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Send a Message
        </a>
      </div>
    </div>
  );
};

export default ClientDocumentation;
